<template>
  <div class="products-layout-page">
    <!-- 页面顶部横幅：pageTitle 非空时显示，样式与 ArticleList 的 .page-banner 保持一致 -->
    <div class="page-banner" v-if="pageTitle">
      <div class="container banner-inner">
        <h1 class="banner-title">{{ pageTitle }}</h1>
        <el-breadcrumb separator="/" class="banner-breadcrumb">
          <el-breadcrumb-item :to="{ path: '/' }">{{ $t('m.home') }}</el-breadcrumb-item>
          <el-breadcrumb-item>{{ pageTitle }}</el-breadcrumb-item>
        </el-breadcrumb>
      </div>
    </div>

    <!-- 主体：一个子分类 = 一个横幅 + 一片产品网格 -->
    <div class="container page-content">
      <div class="pl-inner" :style="{ '--cols': cols }">
        <section class="pl-section" v-for="(sec, si) in sections" :key="sec.id || si">
          <h2 class="pl-bar">
            <span class="pl-bar-text">{{ sec.name }}</span>
          </h2>

          <!-- 产品网格：固定列数 + 自动换行，数量不定也不会错位 -->
          <div class="pl-grid" v-if="sec.items.length">
            <div
              class="pl-item"
              v-for="(item, ii) in sec.items"
              :key="item.id || ii"
              @click="toDetail(sec, item)"
            >
              <span class="pl-thumb">
                <img :src="fixUrl(item.url_image)" :alt="item.title_name">
              </span>
              <span class="pl-name">{{ item.title_name }}</span>
            </div>
          </div>

          <!-- 该子分类下暂无产品 -->
          <div class="pl-empty-one" v-else>
            {{ loading ? $t('m.loading') : $t('m.noContent') }}
          </div>
        </section>

        <!-- 无任何子分类 -->
        <div class="pl-empty" v-if="!sections.length">
          <i class="el-icon-document"></i>
          <p>{{ loading ? $t('m.loading') : $t('m.noContent') }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import url from "../request/base";
import { fixUrl as fixImgUrl } from "../utils/site";

export default {
  name: 'ProductsLayout',
  props: {
    // 顶级分类 ID（产品中心为 11）。sidebar 为空时用它自己去拉子分类（兜底）
    categoryId: {
      type: [String, Number],
      default: 0
    },
    // 页面标题：非空时渲染顶部横幅（与其它栏目页头一致）
    pageTitle: {
      type: String,
      default: ''
    },
    // 子分类列表 [{id, name}]，传入则按它渲染；与 ArticleList 的 sidebar 同款
    sidebar: {
      type: Array,
      default: () => []
    },
    // 每行产品数，横幅宽度会自动跟随
    cols: {
      type: Number,
      default: 4
    },
    // 每个子分类最多显示几个产品；<=0 表示不限（一次全取）
    limit: {
      type: Number,
      default: 0
    },
    // 产品详情页路径
    detailPath: {
      type: String,
      default: '/salesdepartmentukbranch/ukbranddetail'
    }
  },
  data() {
    return {
      imgurl: url.baseUrlimg,
      // [{ id, name, items: [] }]
      sections: [],
      loading: false,
      // 请求序号：父级 props 变化会触发多次重建，用它丢弃过期结果，避免旧数据覆盖新数据
      reqSeq: 0
    }
  },
  computed: {
    // limit <= 0 表示"不限"：给接口一个足够大的值，让它一次返回全部
    queryLimit() {
      return this.limit > 0 ? this.limit : 999
    }
  },
  watch: {
    // 子分类异步到达 → 重建分组（immediate 保证首次渲染就有数据）
    sidebar: {
      handler() {
        this.rebuild()
      },
      immediate: true
    },
    // 切换顶级分类 → 重建
    categoryId() {
      this.rebuild()
    }
  },
  methods: {
    // 拼接图片地址：后端若已返回完整 URL 则不再拼前缀
    fixUrl(path) {
      return fixImgUrl(this.imgurl, path)
    },

    // 重建：先定分类清单，再并行拉每类的产品
    async rebuild() {
      const seq = ++this.reqSeq

      let cats
      if (Array.isArray(this.sidebar) && this.sidebar.length) {
        // 优先用外部传入的子分类（与 ArticleList 的 sidebar 同一份数据）
        cats = this.sidebar.map(c => ({ id: c.id, name: c.name }))
      } else if (this.categoryId) {
        // 兜底：外部没传 sidebar 时，自己按顶级分类 id 拉子分类
        cats = await this.fetchSubCates()
        if (seq !== this.reqSeq) return
      } else {
        this.sections = []
        this.loading = false
        return
      }

      // 先用空 items 占位：横幅先出来，产品到达后逐个填充
      this.sections = cats.map(c => ({ id: c.id, name: c.name, items: [] }))
      if (!this.sections.length) {
        this.loading = false
        return
      }

      this.loading = true
      // 并行拉取：用下标回写（$set 保证响应式），避免多个请求返回顺序不一致导致内容串位
      await Promise.all(
        cats.map((cat, index) =>
          this.fetchProducts(cat.id).then(items => {
            if (seq !== this.reqSeq) return
            this.$set(this.sections, index, { id: cat.id, name: cat.name, items })
          })
        )
      )
      if (seq !== this.reqSeq) return
      this.loading = false
    },

    // 拉某个子分类下的产品列表
    async fetchProducts(id) {
      try {
        const res = await this.$api.home.getcList({
          id,
          limit: this.queryLimit,
          page: 1
        })
        const list = res && res.data && res.data.list
        return Array.isArray(list) ? list : []
      } catch (err) {
        console.error('ProductsLayout fetchProducts error:', err)
        return []
      }
    },

    // 兜底：按 categoryId 拉子分类（只取菜单项，与视图里的过滤规则一致）
    async fetchSubCates() {
      try {
        const res = await this.$api.home.getnavList({ lang: 'zn' })
        let cat = res && res.data && res.data.category
        if (!Array.isArray(cat)) {
          cat = Array.isArray(res && res.data) ? res.data : Object.values((res && res.data) || {})
        }
        if (!Array.isArray(cat)) return []
        return cat
          .filter(c => Number(c.pid) === Number(this.categoryId) && Number(c.is_menu) === 1)
          .map(c => ({ id: c.id, name: c.name }))
      } catch (err) {
        console.error('ProductsLayout fetchSubCates error:', err)
        return []
      }
    },

    // 跳详情：id = 内容 id，pid = 分类 id
    // ⚠️ UKbranchDetail.vue 把 query.pid 当"分类 id"用（:categoryId="pid"），
    //    这里必须传子分类 id，否则详情页的分类归属与面包屑会指错。
    toDetail(sec, item) {
      this.$router.push({
        path: this.detailPath,
        query: {
          id: item.id,
          name: item.title_name,
          pname: sec.name,
          pid: sec.id
        }
      })
    }
  }
}
</script>

<style scoped>
.products-layout-page {
  min-width: 1200px;
  background-color: var(--color-bg-light);
}

/* ========== 页面横幅（复刻 ArticleList，保证各栏目页头观感一致） ========== */
.page-banner {
  background: linear-gradient(135deg, var(--color-primary-dark) 0%, var(--color-primary) 100%);
  padding: 50px 0;
  position: relative;
  overflow: hidden;
}

.page-banner::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -10%;
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  border-radius: 50%;
}

.banner-inner {
  position: relative;
  z-index: 1;
}

.banner-title {
  font-size: 36px;
  font-weight: var(--font-weight-bold);
  color: var(--color-text-inverse);
  margin: 0 0 16px 0;
  letter-spacing: 4px;
}

.banner-breadcrumb {
  font-size: var(--font-size-sm);
}

.banner-breadcrumb :deep(.el-breadcrumb__inner) {
  color: rgba(255, 255, 255, 0.7);
}

.banner-breadcrumb :deep(.el-breadcrumb__item:last-child .el-breadcrumb__inner) {
  color: var(--color-text-inverse);
  font-weight: var(--font-weight-medium);
}

.banner-breadcrumb :deep(.el-breadcrumb__separator) {
  color: rgba(255, 255, 255, 0.5);
}

/* ========== 主体 ========== */
.page-content {
  padding: 50px 0 70px;
  min-height: 400px;
}

/* 布局参数集中在此，改数值即可整体调整（cols 由 props 注入，见 template 的 :style） */
.pl-inner {
  --cols: 4;            /* 每行产品数：由 props.cols 通过 :style 覆盖，此处为兜底值 */
  --cell-w: 250px;      /* 单个产品格子宽 */
  --thumb: 180px;       /* 圆形产品图直径 */
  --bar-h: 80px;        /* 横幅高 */
  --bar-text: 21px;     /* 横幅字号 */
  --name-font: 26px;    /* 产品名字号 */
  --bar-gap: 42px;      /* 横幅底 → 首行圆顶 */
  --name-gap: 20px;     /* 圆底 → 产品名 */
  --row-gap: 48px;      /* 行间距：产品名底 → 下一行圆顶 */
  --sec-gap: 76px;      /* 组间距：上一组末尾 → 下一个横幅 */
  --zoom: 1.16;         /* hover 时产品图放大倍率 */

  /* 内容宽 = 每行产品数 × 格子宽，与横幅天然等宽、无需手填 */
  width: calc(var(--cols) * var(--cell-w));
  margin: 0 auto;
}

.pl-section {
  margin-bottom: var(--sec-gap);
}

.pl-section:last-child {
  margin-bottom: 0;
}

/* ---------- 子分类横幅 ----------
   文字由后端子分类名注入；统一主题色底，各组不交替、层级干净。 */
.pl-bar {
  height: var(--bar-h);
  margin: 0 0 var(--bar-gap) 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-accent) ; /**/
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
}

.pl-bar-text {
  color: var(--color-text-inverse);
  font-size: var(--bar-text);
  letter-spacing: 4px;
  /* 抵消 letter-spacing 在末字后多出的 4px 空隙，让文字真正居中 */
  padding-left: 4px;
}

/* ---------- 产品网格 ---------- */
.pl-grid {
  display: grid;
  grid-template-columns: repeat(var(--cols), var(--cell-w));
  justify-content: center;
  row-gap: var(--row-gap);
}

.pl-item {
  width: var(--cell-w);
  text-align: center;
  cursor: pointer;
}

/* 圆形显示区：正圆裁剪，方形照片自动适配
   换任意真实产品图都无需调整；object-fit:cover 保证不变形。 */
.pl-thumb {
  display: block;
  width: var(--thumb);
  height: var(--thumb);
  margin: 0 auto;
  border-radius: 50%;
  overflow: hidden;
  background-color: var(--color-bg-white);
}

.pl-thumb img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform var(--transition-normal);
}

.pl-item:hover .pl-thumb img {
  transform: scale(var(--zoom));
}

.pl-name {
  display: block;
  margin-top: var(--name-gap);
  font-size: var(--name-font);
  line-height: 1.3;
  color: var(--color-text-regular);
  overflow-wrap: anywhere;   /* 长产品名换行，不撑破格子 */
  transition: color var(--transition-fast);
}

.pl-item:hover .pl-name {
  color: var(--color-primary);
}

/* ========== 空状态 ========== */
.pl-empty-one {
  padding: 30px 0;
  text-align: center;
  font-size: var(--font-size-sm);
  color: var(--color-text-placeholder);
}

.pl-empty {
  text-align: center;
  padding: 80px 0;
  color: var(--color-text-placeholder);
}

.pl-empty i {
  font-size: 64px;
  margin-bottom: 20px;
  display: block;
}

.pl-empty p {
  font-size: var(--font-size-md);
  margin: 0;
}
</style>
